/**
 * Contrast maths for the one colour this app does not control: the brand colour a customer
 * picks for their card. A free hex picker can produce `#FFFFFF`, `#FFEB3B` or `#0B0B0B`, and
 * every one of those breaks a surface that assumes "brand fill, white text" or "brand text on
 * white paper". These helpers answer the two questions that come up:
 *
 *  - what colour does text take **on** a brand fill?      → `onColor()`
 *  - the brand itself is the text/rule — is it legible?   → `ensureContrast()`
 *
 * Everything is WCAG 2.1 relative luminance, so 4.5 is the AA threshold for body text, 3 for
 * large text and for the boundary of a control (1.4.11 Non-text Contrast).
 */

export type Rgb = [number, number, number]

/** WCAG AA for text at normal size. */
export const AA_TEXT = 4.5
/** WCAG AA for large text, icons, rules and control boundaries. */
export const AA_LARGE = 3

const HEX_SHORT = /^#?([\da-f])([\da-f])([\da-f])$/i
const HEX_LONG = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i
const RGB_FN = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i

const clamp = (value: number, min = 0, max = 255) => Math.min(max, Math.max(min, value))

/** Accepts `#abc`, `#aabbcc`, `rgb(…)`, `rgba(…)` and `170 187 204` (the token format). */
export const parseColor = (value?: string | null): Rgb | null => {
  if (!value) return null
  const input = value.trim()

  const short = input.match(HEX_SHORT)
  if (short) return [1, 2, 3].map((i) => parseInt(short[i] + short[i], 16)) as Rgb

  const long = input.match(HEX_LONG)
  if (long) return [1, 2, 3].map((i) => parseInt(long[i], 16)) as Rgb

  const fn = input.match(RGB_FN)
  if (fn) return [1, 2, 3].map((i) => clamp(Math.round(Number(fn[i])))) as Rgb

  const channels = input.split(/[\s,]+/).map(Number)
  if (channels.length === 3 && channels.every((c) => Number.isFinite(c))) {
    return channels.map((c) => clamp(Math.round(c))) as Rgb
  }

  return null
}

export const toHex = ([r, g, b]: Rgb): string =>
  `#${[r, g, b].map((c) => clamp(Math.round(c)).toString(16).padStart(2, '0')).join('')}`.toUpperCase()

const channelLuminance = (channel: number) => {
  const c = channel / 255
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

/** WCAG relative luminance, 0 (black) → 1 (white). */
export const relativeLuminance = (rgb: Rgb): number =>
  0.2126 * channelLuminance(rgb[0]) +
  0.7152 * channelLuminance(rgb[1]) +
  0.0722 * channelLuminance(rgb[2])

const ratioOf = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)

/** WCAG contrast ratio between two colours, 1 (identical) → 21 (black on white). */
export const contrastRatio = (a?: string | null, b?: string | null): number => {
  const rgbA = parseColor(a)
  const rgbB = parseColor(b)
  if (!rgbA || !rgbB) return 1
  return ratioOf(relativeLuminance(rgbA), relativeLuminance(rgbB))
}

/**
 * Flattens a translucent colour over an opaque one, so contrast can be measured against what
 * the eye actually sees rather than against the layer underneath.
 */
export const mixColor = (color: string, onto: string, weight: number): string => {
  const top = parseColor(color)
  const bottom = parseColor(onto)
  if (!top || !bottom) return color
  const w = Math.min(1, Math.max(0, weight))
  return toHex(top.map((c, i) => c * w + bottom[i] * (1 - w)) as Rgb)
}

/** Near-black rather than pure black: it reads as ink, not as a hole in the fill. */
export const INK_ON_LIGHT = '#101114'
export const INK_ON_DARK = '#FFFFFF'

/**
 * The ratio at which white stops being the label on a coloured fill.
 *
 * A straight "whichever ratio is higher" comparison flips to near-black over hundredths of a
 * point, and WCAG 2.x luminance maths systematically under-rates white on a saturated blue: it
 * scores `#0084FF` at 3.66 against white and 5.16 against ink, so a bright blue button would
 * take dark text nobody would ever draw by hand. The app's own primary (`#316FFF`) and danger
 * (`#EF4444`) fills carry white labels for exactly that reason, so a customer's blue has to as
 * well. Above this floor white stays; below it — the yellows, greens, cyans and pale tints that
 * genuinely can't hold white — ink takes over.
 */
export const ON_LIGHT_FLOOR = 3.5

/**
 * The foreground for text or an icon sitting **on** `background`: white unless white is
 * genuinely illegible there, in which case near-black. So a `#0084FF` brand keeps a white label
 * like the system button does, a `#FFEB3B` brand gets ink, and `#0B0B0B` gets white — the caller
 * never has to hard-code `text-white` again.
 */
export const onColor = (
  background?: string | null,
  { light = INK_ON_DARK, dark = INK_ON_LIGHT }: { light?: string; dark?: string } = {}
): string => {
  const rgb = parseColor(background)
  if (!rgb) return light
  const lum = relativeLuminance(rgb)
  const onLight = ratioOf(lum, relativeLuminance(parseColor(light) as Rgb))
  if (onLight >= ON_LIGHT_FLOOR) return light
  return onLight >= ratioOf(lum, relativeLuminance(parseColor(dark) as Rgb)) ? light : dark
}

const rgbToHsl = ([r, g, b]: Rgb): [number, number, number] => {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255]
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  const h =
    max === rn
      ? ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6
      : max === gn
        ? ((bn - rn) / d + 2) / 6
        : ((rn - gn) / d + 4) / 6
  return [h, s, l]
}

const hueToRgb = (p: number, q: number, t: number) => {
  const tt = t < 0 ? t + 1 : t > 1 ? t - 1 : t
  if (tt < 1 / 6) return p + (q - p) * 6 * tt
  if (tt < 1 / 2) return q
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
  return p
}

const hslToRgb = ([h, s, l]: [number, number, number]): Rgb => {
  if (s === 0) return [l * 255, l * 255, l * 255]
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  return [hueToRgb(p, q, h + 1 / 3), hueToRgb(p, q, h), hueToRgb(p, q, h - 1 / 3)].map(
    (c) => c * 255
  ) as Rgb
}

const contrastCache = new Map<string, string>()

/**
 * Returns `color` unchanged when it already clears `minRatio` against `background`; otherwise the
 * same hue and saturation at the nearest lightness that does. Nearest in both directions, so a
 * pale brand on white paper darkens and a near-black brand on a dark sheet lightens — the colour
 * stays recognisably the customer's, it just stops being invisible.
 */
export const ensureContrast = (
  color?: string | null,
  background = '#FFFFFF',
  minRatio: number = AA_TEXT
): string => {
  const rgb = parseColor(color)
  const bg = parseColor(background)
  if (!rgb || !bg) return color ?? INK_ON_LIGHT

  const bgLum = relativeLuminance(bg)
  // A hair of tolerance at the boundary: a colour that lands on 4.4999 is the ratio's intent,
  // and nudging it would repaint the design's own defaults for no visible gain.
  if (ratioOf(relativeLuminance(rgb), bgLum) >= minRatio - 0.01) return toHex(rgb)

  const key = `${toHex(rgb)}|${toHex(bg)}|${minRatio}`
  const cached = contrastCache.get(key)
  if (cached) return cached

  const [h, s, l] = rgbToHsl(rgb)
  const STEP = 0.01
  let darker: string | null = null
  let lighter: string | null = null

  // Candidates are rounded to the 8-bit channels they will actually be painted in before the
  // ratio is measured, so the returned hex really clears the threshold rather than its float.
  const settle = (lightness: number): string | null => {
    const candidate = parseColor(toHex(hslToRgb([h, s, lightness]))) as Rgb
    return ratioOf(relativeLuminance(candidate), bgLum) >= minRatio ? toHex(candidate) : null
  }

  for (let offset = STEP; offset <= 1; offset += STEP) {
    if (!darker && l - offset >= 0) darker = settle(l - offset)
    if (!lighter && l + offset <= 1) lighter = settle(l + offset)
    if (darker || lighter) break
  }

  // Neither direction reached the ratio within the hue — fall back to the ink that does.
  const resolved = darker ?? lighter ?? onColor(background)
  contrastCache.set(key, resolved)
  return resolved
}
